import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Avatar } from 'primereact/avatar';
import { GlobalInputField } from '../../components/GlobalComponents/GlobalInputField';
import { GlobalDropdownField, DropdownOption } from '../../components/GlobalComponents/GlobalDropdownField';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { useNavigate } from 'react-router-dom';
import TeacherBatchAssignmentModal from './parts/TeacherBatchAssignmentModal';
import { useOperatorProfileStore } from '../../stateManagers/stores/operatorProfileStore';

interface ProfilePicture {
  file_name: string;
  key: string;
  cdn_url: string;
  dev_url: string;
  size: number;
  mime_type: string;
  etag: string;
  uploaded_at: {
    $date: {
      $numberLong: string;
    };
  };
}

interface Teacher {
  teacher_id: string;
  name: string;
  dateOfBirth: string | null;
  gender: string | null;
  aadharNumber: string | null;
  category: string | null;
  mobileNumber: string | null;
  email: string;
  district: string | null;
  state: string | null;
  pinCode: string | null;
  maritalStatus: string | null;
  qualification: string | null;
  dateOfJoining: string | null;
  employeeId: string | null;
  experience: string | null;
  bloodGroup: string | null;
  whatsappNumber: string | null;
  status: string;
  created_at: string | null;
  profilePicture?: ProfilePicture | null;
  is_registered: boolean;
}

interface ApiResponse {
  success: boolean;
  data: Teacher[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

const Teachers: React.FC = () => {
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const navigate = useNavigate();
  const { profile, loading: profileLoading } = useOperatorProfileStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [tempSearch, setTempSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, pages: 0 });
  const [selectedTeachers, setSelectedTeachers] = useState<Teacher[] | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [showBatchAssignmentModal, setShowBatchAssignmentModal] = useState(false);
  const [selectedTeacherId, setSelectedTeacherId] = useState<string | null>(null);
  const [selectedTeacherName, setSelectedTeacherName] = useState<string | null>(null);

  const categories: DropdownOption[] = [
    { label: 'All', value: null },
    { label: 'General', value: 'General' },
    { label: 'OBC', value: 'OBC' },
    { label: 'SC', value: 'SC' },
    { label: 'ST', value: 'ST' }
  ];

  const statuses: DropdownOption[] = [
    { label: 'All', value: null },
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' }
  ];

  const limitOptions: DropdownOption[] = [
    { label: 'Show 5', value: 5 },
    { label: 'Show 10', value: 10 },
    { label: 'Show 20', value: 20 },
    { label: 'Show 50', value: 50 }
  ];

  useEffect(() => {
    fetchTeachers();
  }, [page, limit, search]);

  const fetchTeachers = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search && { search })
      });
      const response = await apiClient.get<ApiResponse>(`/v1/teachers/get-teachers?${params}`);
      if (response.data.success) {
        setTeachers(response.data.data);
        setPagination(response.data.pagination);
      } else {
        setError('Failed to load teachers');
      }
    } catch (err: any) {
      console.error('Failed to fetch teachers:', err);
      setError(err.response?.data?.error || 'Failed to load teachers');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => { setSearch(tempSearch); setPage(1); };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => setTempSearch(e.target.value);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return dateString; }
  };

  const handleAssignBatches = (teacherId: string, teacherName: string) => {
    setSelectedTeacherId(teacherId);
    setSelectedTeacherName(teacherName);
    setShowBatchAssignmentModal(true);
  };

  const handleBatchAssignmentSuccess = () => { console.log('Batches assigned successfully'); };

  const colHeaderStyle: React.CSSProperties = {
    background: '#fafafa',
    color: '#9ca3af',
    fontSize: '0.72rem',
    fontWeight: '700',
    textTransform: 'uppercase',
    borderBottom: '2px solid #E5E7EB',
    padding: '14px 16px',
    letterSpacing: '0.6px'
  };
  const colBodyStyle: React.CSSProperties = {
    padding: '14px 16px',
    borderBottom: '1px solid #f3f4f6'
  };

  const profilePictureTemplate = (rowData: Teacher) => {
    const profilePic = rowData.profilePicture;
    const hasPicture = profilePic && !!profilePic.cdn_url;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ position: 'relative' }}>
          <Avatar
            image={hasPicture ? profilePic.cdn_url : undefined}
            icon={!hasPicture ? 'pi pi-user' : undefined}
            size="normal"
            shape="circle"
            style={{
              width: '40px',
              height: '40px',
              backgroundColor: hasPicture ? 'transparent' : '#E8EEF7',
              color: '#0e225d',
              border: '2px solid #E5E7EB',
              boxShadow: '0 2px 8px rgba(14,34,93,0.1)'
            }}
          />
          {rowData.is_registered && (
            <div style={{
              position: 'absolute', bottom: '-2px', right: '-2px',
              width: '14px', height: '14px',
              background: '#16a34a',
              borderRadius: '50%',
              border: '2px solid white',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <i className="pi pi-check" style={{ fontSize: '0.5rem', color: 'white' }}></i>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ color: '#0e225d', fontSize: '0.85rem', fontWeight: '600' }}>
            {rowData.name}
          </span>
        </div>
      </div>
    );
  };

  const statusBodyTemplate = (rowData: Teacher) => (
    <span style={{
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '0.72rem',
      fontWeight: '700',
      letterSpacing: '0.5px',
      textTransform: 'capitalize',
      display: 'inline-block',
      ...(rowData.status === 'active'
        ? { background: 'rgba(22,163,74,0.12)', color: '#15803d', border: '1px solid rgba(22,163,74,0.3)' }
        : { background: 'rgba(239,68,68,0.1)', color: '#c62828', border: '1px solid rgba(239,68,68,0.3)' })
    }}>
      {rowData.status}
    </span>
  );

  const registrationStatusTemplate = (rowData: Teacher) => (
    <span style={{
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '0.72rem',
      fontWeight: '700',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      ...(rowData.is_registered
        ? { background: 'rgba(14,34,93,0.08)', color: '#0e225d', border: '1px solid rgba(14,34,93,0.2)' }
        : { background: 'rgba(251,178,31,0.12)', color: '#b8860b', border: '1px solid rgba(251,178,31,0.35)' })
    }}>
      <i className={rowData.is_registered ? 'pi pi-check-circle' : 'pi pi-exclamation-triangle'}
         style={{ fontSize: '0.7rem' }}></i>
      {rowData.is_registered ? 'Registered' : 'Not Registered'}
    </span>
  );

  const actionBodyTemplate = (rowData: Teacher) => (
    <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
      <button
        onClick={() => navigate(`/teachers/${rowData.teacher_id}`)}
        title="View Details"
        style={{
          width: '32px', height: '32px',
          background: 'transparent', border: '1px solid transparent',
          borderRadius: '8px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#9ca3af', transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(14,34,93,0.08)';
          e.currentTarget.style.borderColor = 'rgba(14,34,93,0.2)';
          e.currentTarget.style.color = '#0e225d';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.borderColor = 'transparent';
          e.currentTarget.style.color = '#9ca3af';
        }}
      >
        <i className="pi pi-eye" style={{ fontSize: '0.85rem' }}></i>
      </button>

      <button
        onClick={() => navigate(`/teachers/edit/${rowData.teacher_id}`)}
        title="Edit Teacher"
        style={{
          width: '32px', height: '32px',
          background: 'transparent', border: '1px solid transparent',
          borderRadius: '8px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#9ca3af', transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(251,178,31,0.12)';
          e.currentTarget.style.borderColor = 'rgba(251,178,31,0.35)';
          e.currentTarget.style.color = '#b8860b';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.borderColor = 'transparent';
          e.currentTarget.style.color = '#9ca3af';
        }}
      >
        <i className="pi pi-pencil" style={{ fontSize: '0.85rem' }}></i>
      </button>

      <button
        onClick={() => handleAssignBatches(rowData.teacher_id, rowData.name)}
        title="Assign Batches"
        style={{
          width: '32px', height: '32px',
          background: 'transparent', border: '1px solid transparent',
          borderRadius: '8px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#9ca3af', transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(22,163,74,0.1)';
          e.currentTarget.style.borderColor = 'rgba(22,163,74,0.3)';
          e.currentTarget.style.color = '#15803d';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.borderColor = 'transparent';
          e.currentTarget.style.color = '#9ca3af';
        }}
      >
        <i className="pi pi-users" style={{ fontSize: '0.85rem' }}></i>
      </button>
    </div>
  );

  const renderPaginationNumbers = () => {
    const pages: (number | string)[] = [];
    const totalPages = pagination.pages;

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (page <= 3) pages.push(1, 2, 3, '...', totalPages);
      else if (page >= totalPages - 2) pages.push(1, '...', totalPages - 2, totalPages - 1, totalPages);
      else pages.push(1, '...', page, '...', totalPages);
    }

    return pages.map((p, idx) =>
      p === '...' ? (
        <span key={`e-${idx}`} style={{ padding: '0 6px', color: '#9ca3af', fontSize: '0.85rem' }}>…</span>
      ) : (
        <button
          key={p}
          onClick={() => setPage(p as number)}
          style={{
            minWidth: '36px', height: '36px', padding: '0 10px',
            background: page === p ? 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)' : 'transparent',
            color: page === p ? '#fbb21f' : '#0e225d',
            border: page === p ? 'none' : '1px solid #E5E7EB',
            borderRadius: '8px',
            fontSize: '0.85rem', fontWeight: page === p ? '700' : '500',
            cursor: 'pointer',
            boxShadow: page === p ? '0 2px 8px rgba(14,34,93,0.25)' : 'none',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => { if (page !== p) { e.currentTarget.style.background = 'rgba(14,34,93,0.06)'; e.currentTarget.style.borderColor = 'rgba(14,34,93,0.2)'; } }}
          onMouseLeave={(e) => { if (page !== p) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = '#E5E7EB'; } }}
        >
          {p}
        </button>
      )
    );
  };

  if (loading && teachers.length === 0) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        minHeight: '400px', background: 'white', borderRadius: '16px',
        border: '1px solid #E5E7EB'
      }}>
        <div style={{ textAlign: 'center' }}>
          <ProgressSpinner style={{ width: '48px', height: '48px' }} strokeWidth="3" animationDuration=".8s" />
          <p style={{ marginTop: '16px', color: '#0e225d', fontSize: '0.95rem', fontWeight: '500' }}>
            Loading teachers...
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .teachers-dt .p-datatable-thead > tr > th {
          background: #fafafa !important;
          color: #9ca3af !important;
          font-size: 0.72rem !important;
          font-weight: 700 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.6px !important;
          border-bottom: 2px solid #E5E7EB !important;
          padding: 14px 16px !important;
        }
        .teachers-dt .p-datatable-tbody > tr {
          transition: background 0.15s ease;
        }
        .teachers-dt .p-datatable-tbody > tr:hover {
          background: rgba(14, 34, 93, 0.03) !important;
        }
        .teachers-dt .p-datatable-tbody > tr > td {
          border-bottom: 1px solid #f3f4f6 !important;
          padding: 14px 16px !important;
        }
        .teachers-dt .p-checkbox .p-checkbox-box.p-highlight {
          background: #0e225d !important;
          border-color: #0e225d !important;
        }
        .teachers-dt .p-checkbox .p-checkbox-box:hover {
          border-color: #fbb21f !important;
        }
      `}</style>

      <div style={{ padding: '0', margin: '0 auto' }}>

        <div style={{
          background: 'linear-gradient(180deg, #0e225d 0%, #1a3370 50%, #0a1a3d 100%)',
          borderRadius: '20px',
          padding: '32px',
          marginBottom: '24px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '2px 0 12px rgba(14, 34, 93, 0.3)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '20px'
        }}>
          <div style={{
            position: 'absolute', top: '-60px', right: '-60px',
            width: '220px', height: '220px',
            background: 'rgba(251,178,31,0.08)',
            borderRadius: '50%', filter: 'blur(50px)', pointerEvents: 'none'
          }} />
          <div style={{
            position: 'absolute', bottom: '-40px', left: '30%',
            width: '140px', height: '140px',
            background: 'rgba(251,178,31,0.05)',
            borderRadius: '50%', filter: 'blur(40px)', pointerEvents: 'none'
          }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 12px',
              background: 'rgba(251,178,31,0.18)',
              border: '1px solid rgba(251,178,31,0.35)',
              borderRadius: '20px',
              marginBottom: '12px',
              fontSize: '0.68rem',
              fontWeight: '700',
              letterSpacing: '0.8px',
              textTransform: 'uppercase',
              color: '#fbb21f'
            }}>
              <i className="pi pi-users" style={{ fontSize: '0.7rem' }}></i>
              Teacher Management
            </div>
            <h1 style={{
              margin: '0 0 8px 0',
              fontSize: '1.75rem',
              fontWeight: '700',
              color: 'white',
              letterSpacing: '-0.5px'
            }}>
              Teachers
            </h1>
            <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)', fontSize: '0.88rem' }}>
              {pagination.total} total teacher{pagination.total !== 1 ? 's' : ''}
            </p>
          </div>

          <div style={{ position: 'relative', zIndex: 1 }}>
            <button
              onClick={() => navigate('/teachers/add-teacher')}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                padding: '12px 24px',
                background: '#fbb21f',
                border: 'none', borderRadius: '12px',
                color: '#0e225d',
                fontSize: '0.88rem', fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(251,178,31,0.4)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                letterSpacing: '0.3px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(251,178,31,0.5)';
                e.currentTarget.style.background = '#ffc93c';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(251,178,31,0.4)';
                e.currentTarget.style.background = '#fbb21f';
              }}
            >
              <i className="pi pi-plus" style={{ fontSize: '0.9rem' }}></i>
              ADD TEACHER
            </button>
          </div>
        </div>

        {error && (
          <Message severity="error" text={error}
            style={{ marginBottom: '20px', width: '100%', fontSize: '0.9rem' }} />
        )}

        <div style={{
          background: 'white',
          padding: '20px 24px',
          borderRadius: '16px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(14,34,93,0.06)',
          border: '1px solid #E5E7EB'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <div style={{
              width: '4px', height: '18px',
              background: 'linear-gradient(180deg, #fbb21f 0%, #e09a00 100%)',
              borderRadius: '4px'
            }} />
            <span style={{ fontSize: '0.82rem', fontWeight: '700', color: '#0e225d', letterSpacing: '0.3px' }}>
              Filter & Search
            </span>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr auto auto',
            gap: '16px',
            alignItems: 'end'
          }}>
            <GlobalInputField
              label="What are you looking for?"
              value={tempSearch}
              onChange={handleInputChange}
              placeholder="Search for name, aadhar, employee ID, etc"
              icon="pi pi-search"
            />
            <GlobalDropdownField
              label="Category"
              value={categoryFilter}
              onChange={(val) => setCategoryFilter(val)}
              options={categories}
              placeholder="All"
              icon="pi pi-tag"
            />
            <GlobalDropdownField
              label="Status"
              value={statusFilter}
              onChange={(val) => setStatusFilter(val)}
              options={statuses}
              placeholder="All"
              icon="pi pi-info-circle"
            />

            <button
              title="Advanced Filters"
              style={{
                width: '44px', height: '44px',
                border: '1px solid #E5E7EB',
                borderRadius: '10px',
                background: 'white',
                color: '#9ca3af',
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#E8EEF7';
                e.currentTarget.style.borderColor = '#fbb21f';
                e.currentTarget.style.color = '#0e225d';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.borderColor = '#E5E7EB';
                e.currentTarget.style.color = '#9ca3af';
              }}
            >
              <i className="pi pi-sliders-v"></i>
            </button>

            <button
              onClick={handleSearch}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '0 20px', height: '44px',
                background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)',
                border: 'none', borderRadius: '10px',
                color: 'white',
                fontSize: '0.82rem', fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(14,34,93,0.25)',
                transition: 'all 0.2s ease',
                letterSpacing: '0.3px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 14px rgba(14,34,93,0.35)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(14,34,93,0.25)';
              }}
            >
              <i className="pi pi-search" style={{ fontSize: '0.82rem' }}></i>
              SEARCH
            </button>
          </div>
        </div>

        <div style={{
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 2px 8px rgba(14,34,93,0.06)',
          border: '1px solid #E5E7EB',
          overflow: 'hidden'
        }}>

          <div style={{
            padding: '18px 24px',
            borderBottom: '2px solid #E5E7EB',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: '4px', height: '22px',
                background: 'linear-gradient(180deg, #fbb21f 0%, #e09a00 100%)',
                borderRadius: '4px'
              }} />
              <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#0e225d' }}>
                Teachers Summary
              </h2>
            </div>

            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <span style={{ fontSize: '0.82rem', color: '#9ca3af', fontWeight: '600' }}>Show</span>
              <GlobalDropdownField
                value={limit}
                onChange={(val) => { setLimit(val); setPage(1); }}
                options={limitOptions}
                placeholder="Show 10"
              />

              {selectedTeachers && selectedTeachers.length > 0 && (
                <button
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '8px 16px',
                    background: 'rgba(14,34,93,0.08)',
                    border: '1px solid rgba(14,34,93,0.2)',
                    borderRadius: '10px',
                    color: '#0e225d',
                    fontSize: '0.82rem', fontWeight: '700',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(14,34,93,0.14)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(14,34,93,0.08)'; }}
                >
                  <i className="pi pi-send" style={{ fontSize: '0.82rem' }}></i>
                  DISPATCH SELECTED ({selectedTeachers.length})
                </button>
              )}
            </div>
          </div>

          <DataTable
            value={teachers}
            selection={selectedTeachers}
            onSelectionChange={(e: any) => setSelectedTeachers(e.value)}
            dataKey="teacher_id"
            loading={loading}
            emptyMessage="No teachers found"
            className="teachers-dt"
            style={{ fontSize: '0.85rem' }}
          >
            <Column
              selectionMode="multiple"
              headerStyle={{ ...colHeaderStyle, width: '3rem' }}
              bodyStyle={colBodyStyle}
            />
            <Column
              field="name"
              header="Teacher"
              body={profilePictureTemplate}
              headerStyle={{ ...colHeaderStyle, minWidth: '280px' }}
              bodyStyle={colBodyStyle}
            />
            <Column
              field="employeeId"
              header="Employee ID"
              body={(rowData) => (
                <span style={{ color: '#6b7280', fontSize: '0.83rem', fontFamily: 'monospace' }}>
                  {rowData.employeeId || '-'}
                </span>
              )}
              headerStyle={colHeaderStyle}
              bodyStyle={colBodyStyle}
            />
            <Column
              field="status"
              header="Status"
              body={statusBodyTemplate}
              headerStyle={colHeaderStyle}
              bodyStyle={colBodyStyle}
            />
            <Column
              field="is_registered"
              header="Registration"
              body={registrationStatusTemplate}
              headerStyle={{ ...colHeaderStyle, minWidth: '150px' }}
              bodyStyle={colBodyStyle}
            />
            <Column
              field="gender"
              header="Gender"
              body={(rowData) => (
                <span style={{ color: '#6b7280', fontSize: '0.83rem' }}>
                  {rowData.gender ? rowData.gender.charAt(0).toUpperCase() + rowData.gender.slice(1) : '-'}
                </span>
              )}
              headerStyle={colHeaderStyle}
              bodyStyle={colBodyStyle}
            />
            <Column
              field="dateOfJoining"
              header="Joining Date"
              body={(rowData) => (
                <span style={{ color: '#6b7280', fontSize: '0.83rem' }}>{formatDate(rowData.dateOfJoining)}</span>
              )}
              headerStyle={colHeaderStyle}
              bodyStyle={colBodyStyle}
            />
            <Column
              header="Actions"
              body={actionBodyTemplate}
              headerStyle={{ ...colHeaderStyle, width: '9rem', textAlign: 'center' }}
              bodyStyle={{ ...colBodyStyle, textAlign: 'center' }}
            />
          </DataTable>

          {pagination.pages > 1 && (
            <div style={{
              padding: '18px 24px',
              borderTop: '2px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '12px',
              flexWrap: 'wrap'
            }}>
              <span style={{ fontSize: '0.82rem', color: '#9ca3af' }}>
                Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, pagination.total)} of {pagination.total}
              </span>

              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  style={{
                    width: '36px', height: '36px',
                    background: 'transparent', border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    cursor: page === 1 ? 'not-allowed' : 'pointer',
                    opacity: page === 1 ? 0.4 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.2s ease', color: '#0e225d'
                  }}
                  onMouseEnter={(e) => { if (page !== 1) e.currentTarget.style.background = 'rgba(14,34,93,0.06)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <i className="pi pi-chevron-left" style={{ fontSize: '0.75rem' }}></i>
                </button>

                {renderPaginationNumbers()}

                <button
                  onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                  disabled={page === pagination.pages}
                  style={{
                    width: '36px', height: '36px',
                    background: 'transparent', border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    cursor: page === pagination.pages ? 'not-allowed' : 'pointer',
                    opacity: page === pagination.pages ? 0.4 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.2s ease', color: '#0e225d'
                  }}
                  onMouseEnter={(e) => { if (page !== pagination.pages) e.currentTarget.style.background = 'rgba(14,34,93,0.06)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <i className="pi pi-chevron-right" style={{ fontSize: '0.75rem' }}></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <TeacherBatchAssignmentModal
        visible={showBatchAssignmentModal}
        teacherId={selectedTeacherId || ''}
        teacherName={selectedTeacherName || ''}
        instituteId={profile?.institute_id}
        branchId={profile?.branch_id}
        onHide={() => setShowBatchAssignmentModal(false)}
        onSuccess={handleBatchAssignmentSuccess}
      />
    </>
  );
};

export default Teachers;